import { useEffect, useState, type ComponentType } from 'react'
import { loadOnce, outdatedApp, type ChunkLoader } from '../../app/chunks'
import { Icon } from '../../components/Icon'
import { ScreenHeader } from './ScreenHeader'

/**
 * Screens most people rarely open are chunks of their own, fetched the first time one is shown: settings and the admin
 * screens. (Like the photo viewer, see conversation/photoViewer.ts.)
 */
export const screenSources = {
  settings: (): Promise<ComponentType> => import('../settings/SettingsScreen').then((module) => module.SettingsScreen),
  adminHome: (): Promise<ComponentType> => import('../admin/AdminHome').then((module) => module.AdminHome),
  adminPerson: (): Promise<ComponentType> => import('../admin/PersonScreen').then((module) => module.PersonScreen),
  adminPeople: (): Promise<ComponentType> => import('../admin/PeopleScreen').then((module) => module.PeopleScreen),
  auditLog: (): Promise<ComponentType> => import('../admin/AuditLogScreen').then((module) => module.AuditLogScreen),
  adminDevices: (): Promise<ComponentType> => import('../admin/AdminDevicesScreen').then((module) => module.AdminDevicesScreen),
  adminChannels: (): Promise<ComponentType> => import('../admin/AdminChannelsScreen').then((module) => module.AdminChannelsScreen),
  adminBots: (): Promise<ComponentType> => import('../admin/AdminBotsScreen').then((module) => module.AdminBotsScreen),
  adminIntegrations: (): Promise<ComponentType> => import('../admin/AdminIntegrationsScreen').then((module) => module.AdminIntegrationsScreen),
  adminProvisioning: (): Promise<ComponentType> => import('../admin/AdminProvisioningScreen').then((module) => module.AdminProvisioningScreen),
}

export type ScreenName = keyof typeof screenSources

const titles: Record<ScreenName, string> = {
  settings: 'Settings',
  adminHome: 'Administration',
  adminPeople: 'People',
  adminPerson: 'Person',
  auditLog: 'Audit log',
  adminDevices: 'Devices',
  adminChannels: 'Channels',
  adminBots: 'Bots',
  adminIntegrations: 'Integrations',
  adminProvisioning: 'Provisioning',
}

const loaded = new Map<ScreenName, ComponentType>()
const loaders = new Map<ScreenName, ChunkLoader<ComponentType>>()

function loaderFor(name: ScreenName): ChunkLoader<ComponentType> {
  let loader = loaders.get(name)
  if (!loader) {
    loader = loadOnce({ load: () => screenSources[name]() })
    loaders.set(name, loader)
  }
  return loader
}

/** Loads a screen once. After a failure (offline, or an update replaced the chunk) the next try fetches it again. */
export function loadScreen(name: ScreenName): Promise<ComponentType> {
  return loaderFor(name)().then((screen) => {
    loaded.set(name, screen)
    return screen
  })
}

/**
 * A route's screen from its own chunk. While it loads the pane says so; if it can't be fetched, the rest of the app
 * carries on and the pane offers to try again.
 */
export function LazyScreen({ name }: { name: ScreenName }) {
  // Which screen the last try was for, and how it went; a screen already loaded shows straight away.
  const [tried, setTried] = useState<{ name: ScreenName; failed: boolean; attempt: number } | null>(null)
  const [attempt, setAttempt] = useState(0)
  const Screen = loaded.get(name)

  useEffect(() => {
    if (Screen) return
    let cancelled = false
    loadScreen(name).then(
      () => {
        if (!cancelled) setTried({ name, failed: false, attempt })
      },
      () => {
        if (!cancelled) setTried({ name, failed: true, attempt })
      },
    )
    return () => {
      cancelled = true
    }
  }, [name, Screen, attempt])

  if (Screen) return <Screen />
  const last = tried?.name === name ? tried : null
  const failed = last?.failed === true
  const retrying = failed && last.attempt !== attempt
  // A released version replaces every chunk: once trying again hasn't helped, only opening the app afresh will.
  const outdated = failed && outdatedApp(loaderFor(name))

  return (
    <div className="screen">
      <ScreenHeader title={titles[name]} />
      <div className="chat-empty">
        {failed ? (
          <>
            <Icon name="alert" />
            <h2>Couldn't open {titles[name]}</h2>
            <p>{outdated ? 'A newer version of BOSS Chat may be waiting. Reload the app to open this page.' : 'Check your connection and try again.'}</p>
            {outdated ? (
              <button type="button" className="button primary" onClick={() => window.location.reload()}>
                <Icon name="refresh" />
                Reload the app
              </button>
            ) : (
              <button
                type="button"
                className="button primary"
                // Stays focusable while trying again, so focus stays put.
                aria-disabled={retrying || undefined}
                onClick={() => {
                  if (!retrying) setAttempt((n) => n + 1)
                }}
              >
                <Icon name="refresh" />
                {retrying ? 'Trying again…' : 'Try again'}
              </button>
            )}
          </>
        ) : (
          <span className="spinner" role="status" aria-label={`Loading ${titles[name]}`} />
        )}
      </div>
    </div>
  )
}
